/**
 * Code editor — styled textarea with line numbers and auto-detect on input.
 */

import { store } from './state';
import { detectInput } from '../core/detect';
import type { InputFormat } from '../core/types';

let textarea: HTMLTextAreaElement;
let lineNumbersEl: HTMLElement;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

export function initEditor(): void {
  textarea = document.getElementById('code-editor') as HTMLTextAreaElement;
  lineNumbersEl = document.getElementById('line-numbers') as HTMLElement;
  if (!textarea) return;

  textarea.addEventListener('input', onInput);
  textarea.addEventListener('paste', () => {
    setTimeout(onInput, 10);
  });

  // Sync line numbers on scroll
  textarea.addEventListener('scroll', syncLineNumberScroll);

  // Initial line numbers
  updateLineNumbers();
}

function onInput(): void {
  const text = textarea.value;
  store.set({ input: text });

  // Auto-detect input format
  if (text.trim().length > 0) {
    const detected = detectInput(text);
    store.set({ inputFormat: detected.format as InputFormat });
  }

  updateLineNumbers();

  // Debounced conversion trigger
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    document.dispatchEvent(new CustomEvent('ex-mermaid:convert'));
  }, 500);
}

function updateLineNumbers(): void {
  if (!lineNumbersEl || !textarea) return;
  const lineCount = Math.max(textarea.value.split('\n').length, 1);
  let html = '';
  for (let i = 1; i <= lineCount; i++) {
    html += `<span>${i}</span>`;
  }
  lineNumbersEl.innerHTML = html;
  syncLineNumberScroll();
}

function syncLineNumberScroll(): void {
  if (!lineNumbersEl || !textarea) return;
  lineNumbersEl.scrollTop = textarea.scrollTop;
}

export function setEditorValue(text: string, skipConvert = false): void {
  if (textarea) {
    textarea.value = text;
    store.set({ input: text });
    updateLineNumbers();
    if (skipConvert && debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  }
}
